/**
 * Intercepteurs API centralisés pour l'authentification
 * Gère l'ajout automatique des tokens aux requêtes et le rafraîchissement des tokens
 */

import axios, { AxiosRequestConfig, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { TokenManager } from './token-manager';
import { authService } from './auth-service';
import { auditLogger } from './audit-logger';

// Types
type ErrorHandlerFunction = (error: AxiosError) => Promise<any>;

/**
 * Ajoute le token d'authentification à chaque requête API
 */
export const authRequestInterceptor = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  // Vérifier si TokenManager est disponible
  if (!config) {
    console.warn('⚠️ Configuration de requête non définie');
    return config;
  }

  try {
    // Récupérer le token d'accès
    const accessToken = TokenManager.getAccessToken();
    
    // Si un token est disponible, l'ajouter à toutes les requêtes
    if (accessToken && config.headers) {
      // S'assurer que headers existe
      config.headers = config.headers || {};
      // Ajouter le token d'authentification
      config.headers['Authorization'] = `Token ${accessToken}`;
      
      // Journalisation sécurisée (ne pas afficher le token complet)
      const maskedToken = accessToken.substring(0, 4) + '...' + accessToken.substring(accessToken.length - 4);
      console.log(`🔑 Token d'authentification ajouté à la requête pour: ${config.url} (${maskedToken})`);
      
      // Journalisation d'audit de l'accès à l'API
      if (config.url) {
        auditLogger.logApiAccess({
          url: config.url,
          method: config.method?.toUpperCase() || 'GET',
          authenticated: true
        });
      }
    } else if (config.url && !config.url.includes('/auth/') && !config.url.includes('/token/')) {
      // Indiquer l'absence de token pour les requêtes non-auth
      console.warn(`⚠️ Aucun token d'authentification disponible pour: ${config.url}`);
      
      // Journalisation d'audit de l'accès à l'API non authentifié
      if (config.url) {
        auditLogger.logApiAccess({
          url: config.url,
          method: config.method?.toUpperCase() || 'GET',
          authenticated: false
        });
      }
    }
  } catch (error) {
    console.error('Erreur dans l\'intercepteur d\'authentification:', error);
  }
  
  return config;
};

/**
 * Gère les erreurs d'authentification (401, 403) et tente de rafraîchir le token
 */
export const authErrorInterceptor = async (error: AxiosError): Promise<any> => {
  if (!error.response) {
    return Promise.reject(error);
  }

  // Extraire les informations de la requête pour le retry
  const originalRequest = error.config;
  if (!originalRequest) {
    return Promise.reject(error);
  }

  // Gérer les erreurs d'authentification
  if (error.response.status === 401) {
    console.warn('🔐 Erreur 401: Authentification requise - Tentative de rafraîchissement du token');
    
    // Journaliser l'erreur d'authentification
    auditLogger.logAuthError({
      type: 'token_expired',
      message: 'Token expiré ou invalide',
      statusCode: 401
    });

    // Éviter les boucles infinies de rafraîchissement
    if ((originalRequest as any)._retry) {
      console.error('🔐 Échec après tentative de rafraîchissement du token, déconnexion forcée');
      await authService.logout();
      return Promise.reject(error);
    }

    try {
      // Marquer la requête comme déjà retentée
      (originalRequest as any)._retry = true;
      
      // Tenter de rafraîchir le token
      const refreshSuccess = await authService.refreshToken();
      
      if (refreshSuccess) {
        console.log('🔐 Token rafraîchi avec succès, reprise de la requête originale');
        
        // Obtenir le nouveau token
        const newToken = TokenManager.getAccessToken();
        
        // Mettre à jour le token dans la requête originale
        if (newToken && originalRequest.headers) {
          originalRequest.headers['Authorization'] = `Token ${newToken}`;
        }
        
        // Réessayer la requête originale avec le nouveau token
        return axios(originalRequest);
      } else {
        console.warn('🔐 Échec du rafraîchissement du token, déconnexion forcée');
        await authService.logout();
        return Promise.reject(error);
      }
    } catch (refreshError) {
      console.error('🔐 Erreur lors du rafraîchissement du token:', refreshError);
      await authService.logout();
      return Promise.reject(error);
    }
  } else if (error.response.status === 403) {
    console.warn('🔐 Erreur 403: Accès interdit');
    
    // Journaliser l'erreur d'autorisation
    auditLogger.logAuthError({
      type: 'access_denied',
      message: 'Accès refusé à la ressource',
      statusCode: 403,
      resourceUrl: originalRequest.url
    });
  }

  return Promise.reject(error);
};

/**
 * Configure un client axios avec les intercepteurs d'authentification
 * @param axiosInstance Instance axios à configurer
 */
export const configureAuthInterceptors = (axiosInstance: any): void => {
  // Nettoyer les intercepteurs existants si nécessaire
  // ...

  // Ajouter les intercepteurs d'authentification
  axiosInstance.interceptors.request.use(
    authRequestInterceptor,
    (error: any) => Promise.reject(error)
  );

  axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => response,
    authErrorInterceptor
  );
  
  console.log('✅ Intercepteurs d\'authentification configurés avec succès');
};

// Fonction d'utilitaire pour vérifier si une URL est liée à l'authentification
export const isAuthRelatedUrl = (url: string | undefined): boolean => {
  if (!url) return false;
  
  return (
    url.includes('/token') ||
    url.includes('/auth/') ||
    url.includes('/login') ||
    url.includes('/logout') ||
    url.includes('/refresh')
  );
};

// Exporter un objet avec toutes les fonctions
export const apiInterceptors = {
  authRequestInterceptor,
  authErrorInterceptor,
  configureAuthInterceptors,
  isAuthRelatedUrl
};

export default apiInterceptors;
